import { useQuery } from '@tanstack/react-query'
import { localProfilesQuery, type LocalProfile } from '../api/devices'

/**
 * Cartela "Local profiles" sub piesele din fila Configuration: cine are un
 * dosar de profil pe masina (ProfileList pe Windows, /etc/passwd pe Linux,
 * /Users pe Mac), unde, cand l-a folosit ultima oara, si semnele lui -
 * incarcat acum, temporar, itinerant (roaming), obligatoriu (mandatory).
 *
 * E o cartela in plus, nu fila: fara drept pe listele de aparate (403), fara
 * date de la agent sau cu orice eroare, nu apare deloc - piesele de deasupra
 * raman cum erau.
 */
export function DeviceLocalProfiles({ id }: { id: number }) {
  const { data } = useQuery(localProfilesQuery(id))
  if (!data || data.profiles.length === 0) return null

  return (
    <div className="dcol-grid dcol-grid-span">
      <section className="dcol dcol-span-2" data-group="local-profiles">
        <h3 className="dcol-head" title="User profile folders registered on this machine, as the agent read them with the hardware inventory.">
          <i className="fas fa-user-circle" aria-hidden="true"></i>
          <span className="dcol-name">Local profiles</span>
          <span className="dcol-count">{data.profiles.length}</span>
        </h3>
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th>Account</th>
                <th>Path</th>
                <th>Last used</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {data.profiles.map((p) => (
                <tr key={p.sid} data-sid={p.sid}>
                  <td title={p.sid}>{p.name || <span className="dcf-muted">{p.sid}</span>}</td>
                  <td className="text-break">{p.path}</td>
                  <td>{p.lastUse ? <time dateTime={p.lastUse}>{localTime(p.lastUse)}</time> : <span className="dcf-muted">&ndash;</span>}</td>
                  <td>
                    <Badges profile={p} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function Badges({ profile }: { profile: LocalProfile }) {
  const marks: [boolean, string, string][] = [
    [profile.loaded, 'Loaded', 'badge-success'],
    [profile.temporary, 'Temporary', 'badge-warning'],
    [profile.roaming, 'Roaming', 'badge-info'],
    [profile.mandatory, 'Mandatory', 'badge-secondary'],
  ]
  const shown = marks.filter(([on]) => on)
  if (shown.length === 0) return <span className="dcf-muted">&ndash;</span>

  return (
    <>
      {shown.map(([, label, cls]) => (
        <span key={label} className={`badge ${cls} mr-1`}>
          {label}
        </span>
      ))}
    </>
  )
}

/** ISO in UTC -> "AAAA-LL-ZZ HH:MM" in fusul browserului. */
export function localTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')

  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}
